/**
 * UI-41: Execution Impact Inline
 *
 * Pure display component — shows Before → After metrics immediately after action execution.
 * PILOT-23: Also shows the rooms that were updated when mutation data is available.
 *
 * Rules:
 *   - Always renders all 3 rows (even when values are equal)
 *   - No conditional hiding
 *   - No color coding
 *   - backlog / slaBreaches → integers
 *   - utilization → percentage
 */

import type { MetricsSnapshot, ActionMutation } from '../../services/actionExecutionService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  before: MetricsSnapshot;
  after: MetricsSnapshot;
  /** PILOT-23: Optional mutation list for "Rooms updated" display */
  mutations?: readonly ActionMutation[];
}

// ─── Row definitions — deterministic order ───────────────────────────────────

const ROWS: Array<{
  label: string;
  format: (before: MetricsSnapshot, after: MetricsSnapshot) => string;
}> = [
  {
    label: 'Backlog',
    format: (b, a) => `${b.backlog} → ${a.backlog}`,
  },
  {
    label: 'SLA breaches',
    format: (b, a) => `${b.slaBreaches} → ${a.slaBreaches}`,
  },
  {
    label: 'Utilization',
    format: (b, a) => `${b.utilization}% → ${a.utilization}%`,
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function ExecutionImpactInline({ before, after, mutations }: Props) {
  // PILOT-23: Collect unique room numbers from mutations for display
  const updatedRooms = mutations && mutations.length > 0
    ? [...new Set(mutations.map(m => m.display.roomNumber).filter(Boolean))].join(', ')
    : null;

  return (
    <div className="mt-3 rounded-lg bg-slate-800/60 border border-slate-700/50 px-3 py-2.5">
      <p className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
        Impact
      </p>
      <dl className="space-y-1">
        {ROWS.map(({ label, format }) => (
          <div key={label} className="flex items-baseline justify-between gap-4">
            <dt className="text-[11px] text-slate-400 shrink-0">{label}</dt>
            <dd className="text-[11px] text-slate-200 font-mono tabular-nums">
              {format(before, after)}
            </dd>
          </div>
        ))}
      </dl>
      {updatedRooms && (
        <p data-testid="execution-rooms-updated" className="text-[10px] text-slate-400 mt-2 border-t border-slate-700/50 pt-2">
          Rooms updated: <span className="text-slate-200 font-medium">{updatedRooms}</span>
        </p>
      )}
    </div>
  );
}

